// JQ4-47 — OrganizationDetailPage
//
// Super Admin drill-down for a single org:
//  - Shows full org_admin invitation history (JQ4-47)
//  - Inline edit section for core org fields (JQ4-48)
// GET /super-admin/organizations/:orgId/invitations
// GET /super-admin/organizations/:orgId           (pre-populate edit form)
// PATCH /super-admin/organizations/:orgId         (save edit)

import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  getOrganization,
  listOrganizationInvitations,
  resendOrganizationInvitation,
  updateOrganization,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { InvitationListItem, OrganizationResponse } from '@jq4race/shared';
import {
  DEFAULT_COUNTRY_CODE,
  DEFAULT_TIMEZONE,
  type UpdateOrganizationDto,
  UpdateOrganizationRequestSchema,
} from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@jq4race/ui';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, ShieldAlert, TriangleAlert, X } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { InvitationsTable } from '../../../users/ui/components/InvitationsTable';
import { BILLING_STATUS_LABEL_KEYS, BILLING_STATUS_TONE } from '../billing-status';
import { OrganizationSubscriptionSection } from '../components/OrganizationSubscriptionSection';
import { COUNTRY_OPTIONS, NICHE_OPTIONS, TIMEZONE_OPTIONS } from '../constants';

export function OrganizationDetailPage() {
  const { t } = useTranslation('organizations');
  const { orgId } = useParams({ strict: false });

  const [invitations, setInvitations] = useState<InvitationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Edit form state ─────────────────────────────────────────────────────────
  const [org, setOrg] = useState<OrganizationResponse | null>(null);
  const [orgLoadError, setOrgLoadError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listOrganizationInvitations(orgId);
      setInvitations(res.invitations);
    } catch (e) {
      setSuccess(null);
      setError(translateApiError(e, t, { fallback: t('detail.invitationsSection.loadListError') }));
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  // Load the org data for form pre-population (R7.1)
  const loadOrg = useCallback(async () => {
    if (!orgId) return;
    setOrgLoadError(null);
    try {
      const data = await getOrganization(orgId);
      setOrg(data);
    } catch (e) {
      setOrgLoadError(translateApiError(e, t, { fallback: t('detail.loadOrgError') }));
    }
  }, [orgId, t]);

  useEffect(() => {
    void reload();
    void loadOrg();
  }, [reload, loadOrg]);

  async function handleResend(invitationId: string): Promise<void> {
    if (!orgId) return;
    try {
      await resendOrganizationInvitation(orgId, invitationId);
      setError(null);
      setSuccess(t('detail.invitationsSection.resendSuccess'));
      void reload();
    } catch (e) {
      setSuccess(null);
      setError(translateApiError(e, t, { fallback: t('detail.invitationsSection.resendError') }));
      throw e;
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={
          <Link
            to="/super-admin/organizations"
            className="inline-flex items-center gap-1 text-jq-graphite hover:text-jq-charcoal"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
            {t('detail.backToOrganizations')}
          </Link>
        }
        title={t('detail.title')}
        description={t('detail.description')}
      />

      {error && <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      {/* ── Edit section (JQ4-48) ─────────────────────────────────────────── */}
      {editSuccess && (
        <FeedbackBanner
          tone="success"
          message={editSuccess}
          onDismiss={() => setEditSuccess(null)}
        />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <h2 className="mb-6 text-[15px] font-medium text-jq-charcoal">
            {t('detail.editSection.heading')}
          </h2>
          {orgLoadError ? (
            // R7.2 — do NOT render empty form if org data could not be loaded
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <p className="text-[13px] text-jq-graphite">{t('detail.editSection.loadError')}</p>
              <Button size="sm" variant="secondary" onClick={() => void loadOrg()}>
                {t('detail.editSection.retry')}
              </Button>
            </div>
          ) : org ? (
            <EditOrgForm
              org={org}
              onSaved={(updated) => {
                setOrg(updated);
                setEditSuccess(t('detail.editSection.updateSuccess'));
              }}
            />
          ) : (
            <p className="text-[13px] text-jq-graphite">{t('detail.editSection.loading')}</p>
          )}
        </CardContent>
      </Card>

      {/* ── Invitations section (JQ4-47) ──────────────────────────────────── */}
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <h2 className="mb-6 text-[15px] font-medium text-jq-charcoal">
            {t('detail.invitationsSection.heading')}
          </h2>
          {error && !loading ? (
            <div className="flex flex-col items-center gap-4 py-12 text-center">
              <p className="text-[13px] text-jq-graphite">
                {t('detail.invitationsSection.loadError')}
              </p>
              <Button size="sm" variant="secondary" onClick={() => void reload()}>
                {t('detail.invitationsSection.retry')}
              </Button>
            </div>
          ) : (
            <InvitationsTable invitations={invitations} loading={loading} onResend={handleResend} />
          )}
        </CardContent>
      </Card>

      {/* ── Subscription invoices (manual-subscription-billing, WU8) ──────── */}
      {orgId && <OrganizationSubscriptionSection orgId={orgId} />}
    </div>
  );
}

// ── Edit form component ───────────────────────────────────────────────────────

interface EditOrgFormProps {
  org: OrganizationResponse;
  onSaved: (updated: OrganizationResponse) => void;
}

function EditOrgForm({ org, onSaved }: EditOrgFormProps) {
  const { t } = useTranslation('organizations');
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting, isDirty, dirtyFields },
  } = useForm<UpdateOrganizationDto>({
    resolver: zodResolver(UpdateOrganizationRequestSchema),
    defaultValues: {
      name: org.name,
      legalName: org.legalName ?? '',
      country: org.country ?? DEFAULT_COUNTRY_CODE,
      timezone: org.timezone ?? DEFAULT_TIMEZONE,
      niche: org.niche ?? 'restaurant',
      status: org.status,
    },
  });

  // If a fresh org is loaded (e.g. after save + refresh), reset the form
  useEffect(() => {
    reset({
      name: org.name,
      legalName: org.legalName ?? '',
      country: org.country ?? DEFAULT_COUNTRY_CODE,
      timezone: org.timezone ?? DEFAULT_TIMEZONE,
      niche: org.niche ?? 'restaurant',
      status: org.status,
    });
  }, [org, reset]);

  const [serverError, setServerError] = useState<string | null>(null);
  // QA-I11 — suspending is a full shutdown: hold the patch here until the
  // super_admin explicitly confirms it in a dialog.
  const [pendingSuspendPatch, setPendingSuspendPatch] =
    useState<Partial<UpdateOrganizationDto> | null>(null);

  async function persist(patch: Partial<UpdateOrganizationDto>) {
    try {
      const updated = await updateOrganization(org.id, patch);
      onSaved(updated);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // R3.3 — display name conflict
        setError('name', { type: 'server', message: t('detail.editSection.nameInUse') });
        return;
      }
      setServerError(translateApiError(e, t, { fallback: t('detail.editSection.updateError') }));
    }
  }

  async function onSubmit(values: UpdateOrganizationDto) {
    setServerError(null);
    // Submit ONLY the fields the user actually changed.
    // Null-coerced defaults (country:DEFAULT_COUNTRY_CODE, niche:'restaurant', legalName:'') used for display
    // must NOT be written back to an org that had null for those fields.
    const patch = (Object.keys(dirtyFields) as Array<keyof UpdateOrganizationDto>).reduce<
      Partial<UpdateOrganizationDto>
    >((acc, key) => {
      if (dirtyFields[key]) {
        acc[key] = values[key] as never;
      }
      return acc;
    }, {});
    if (patch.status === 'suspended' && org.status !== 'suspended') {
      setPendingSuspendPatch(patch);
      return;
    }
    await persist(patch);
  }

  async function confirmSuspend() {
    const patch = pendingSuspendPatch;
    setPendingSuspendPatch(null);
    if (patch) await persist(patch);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      {/* billing-status-visibility — shown ABOVE the status control (spec
          Capability 3: "make the suspend action informed"). Deliberately its
          own labeled block, never merged into the Organization.status field
          below, so a super_admin sees the billing state before touching the
          full-shutdown control. */}
      <BillingStatusSummary org={org} />

      <div className="grid gap-6 md:grid-cols-2">
        <Field
          label={t('detail.editSection.fields.legalName.label')}
          htmlFor="legalName"
          error={errors.legalName?.message}
        >
          <Input
            id="legalName"
            placeholder={t('detail.editSection.fields.legalName.placeholder')}
            autoComplete="organization"
            disabled={isSubmitting}
            {...register('legalName')}
          />
        </Field>

        <Field
          label={t('detail.editSection.fields.displayName.label')}
          htmlFor="name"
          error={errors.name?.message}
        >
          <Input
            id="name"
            placeholder={t('detail.editSection.fields.displayName.placeholder')}
            disabled={isSubmitting}
            {...register('name')}
            aria-describedby="name-help"
          />
          <p id="name-help" className="mt-1 text-[12px] text-jq-graphite">
            {t('detail.editSection.fields.displayName.help')}
          </p>
        </Field>

        <Field
          label={t('detail.editSection.fields.country.label')}
          htmlFor="country"
          error={errors.country?.message}
        >
          <SelectInput id="country" disabled={isSubmitting} {...register('country')}>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label} ({c.code})
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('detail.editSection.fields.timezone.label')}
          htmlFor="timezone"
          error={errors.timezone?.message}
        >
          <SelectInput id="timezone" disabled={isSubmitting} {...register('timezone')}>
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('detail.editSection.fields.niche.label')}
          htmlFor="niche"
          error={errors.niche?.message}
        >
          <SelectInput id="niche" disabled={isSubmitting} {...register('niche')}>
            {NICHE_OPTIONS.map((n) => (
              <option key={n.value} value={n.value}>
                {enumLabel(t, 'niche', n.value)}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('detail.editSection.fields.status.label')}
          htmlFor="status"
          error={errors.status?.message}
        >
          <SelectInput id="status" disabled={isSubmitting} {...register('status')}>
            <option value="active">{t('detail.editSection.status.active')}</option>
            <option value="suspended">{t('detail.editSection.status.suspended')}</option>
            <option value="deleted">{t('detail.editSection.status.deleted')}</option>
          </SelectInput>
          {/* billing-status-visibility — the non-negotiable clarification:
              this control is a full shutdown, unrelated to (and never
              auto-synced with) the subscription's own suspended/canceled
              state shown in BillingStatusSummary above. */}
          <p
            role="note"
            className="mt-2 flex items-start gap-1.5 text-[12px] leading-snug text-jq-warning"
          >
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
            <span>{t('detail.editSection.billing.fullShutdownWarning')}</span>
          </p>
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        <Button type="submit" size="sm" disabled={isSubmitting || !isDirty}>
          {isSubmitting ? t('detail.editSection.saving') : t('detail.editSection.save')}
        </Button>
      </div>

      <Dialog
        open={pendingSuspendPatch !== null}
        onOpenChange={(open) => {
          if (!open) setPendingSuspendPatch(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.editSection.suspendConfirm.title')}</DialogTitle>
            <DialogDescription>
              {t('detail.editSection.suspendConfirm.description', { name: org.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPendingSuspendPatch(null)}
            >
              {t('detail.editSection.suspendConfirm.cancel')}
            </Button>
            <Button type="button" size="sm" onClick={() => void confirmSuspend()}>
              {t('detail.editSection.suspendConfirm.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}

// billing-status-visibility — BillingStatusSummary.
//
// Read-only summary of the org's SUBSCRIPTION state, shown above the
// Organization.status control on purpose: this is where a super_admin
// decides whether to suspend the org (spec Capability 3), and today
// nothing on this screen relates that decision to whether the org is
// actually current on its bill. Shows the EFFECTIVE status (accounts for a
// lazily-elapsed past_due grace window / onboarding deadline — see the
// backend's resolveEffectiveSubscriptionStatus) since that's what actually
// applies right now, plus the plan name. `null` means no subscription has
// ever been assigned — a normal, non-alarming state.
function BillingStatusSummary({ org }: { org: OrganizationResponse }) {
  const { t } = useTranslation('organizations');
  const status = org.effectiveSubscriptionStatus;

  return (
    <Card
      data-testid="billing-status-summary"
      className="border-jq-sand/70 bg-jq-surface-0 shadow-sm"
    >
      <CardContent className="flex flex-wrap items-center gap-3 p-4 sm:p-5">
        <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
          {t('detail.editSection.billing.heading')}
        </p>
        {status ? (
          <>
            <Badge variant={BILLING_STATUS_TONE[status]}>
              {t(BILLING_STATUS_LABEL_KEYS[status])}
            </Badge>
            {org.planName && <span className="text-[13px] text-jq-charcoal">{org.planName}</span>}
          </>
        ) : (
          <span className="text-[13px] text-jq-graphite">
            {t('detail.editSection.billing.noPlan')}
          </span>
        )}
      </CardContent>
    </Card>
  );
}

// ── Building blocks ───────────────────────────────────────────────────────────

function Field({
  label,
  htmlFor,
  error,
  className,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className ?? ''}>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const SelectInput = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        {...rest}
        className={`flex h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50 ${
          className ?? ''
        }`}
      />
    );
  },
);

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}

// ── Feedback banner (mirrors OrganizationsPage pattern) ──────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('organizations');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('list.dismissMessage')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
